<template>
  <div :class="wrapperClasses" ref="wrapper">
    <!-- Standard Label (non-floating) -->
    <label v-if="label && !floating" :for="selectId" class="select__label">
      {{ label }}
      <span v-if="required" class="select__required">*</span>
    </label>

    <div class="select__container" @click="toggleDropdown">
      <span v-if="$slots.prepend || prependIcon" class="select__prepend">
        <slot name="prepend">
          <i v-if="prependIcon" :class="prependIcon"></i>
        </slot>
      </span>

      <div class="select__value">
        <span v-if="displayValue" class="select__selected">{{ displayValue }}</span>
        <span v-else-if="!floating" class="select__placeholder">{{ placeholder }}</span>
      </div>

      <!-- Floating Label -->
      <label v-if="label && floating" :for="selectId" class="select__floating-label">
        {{ label }}
        <span v-if="required" class="select__required">*</span>
      </label>

      <span v-if="clearable && internalValue != null" class="select__clear" @click.stop="clearSelection">
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </span>

      <span class="select__arrow" :class="{ 'select__arrow--open': isOpen }">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </span>
    </div>

    <transition name="dropdown">
      <div v-if="isOpen" class="select__dropdown">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="['select__option', { 'select__option--selected': isSelected(item) }]"
          @click="selectItem(item)"
        >
          {{ getItemTitle(item) }}
        </div>
        <div v-if="items.length === 0" class="select__empty">
          {{ emptyText }}
        </div>
      </div>
    </transition>

    <div v-if="hint && !error" class="select__hint">{{ hint }}</div>
    <div v-if="error && errorMessage" class="select__error">{{ errorMessage }}</div>
  </div>
</template>

<script>
let selectIdCounter = 0

export default {
  name: 'BaseSelect',
  props: {
    modelValue: {
      type: [String, Number, Object, Array],
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    itemTitle: {
      type: String,
      default: 'title'
    },
    itemValue: {
      type: String,
      default: 'value'
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: '선택하세요'
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    },
    floating: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String,
      default: null
    },
    emptyText: {
      type: String,
      default: '항목이 없습니다'
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      selectId: `select-${++selectIdCounter}`,
      internalValue: this.modelValue,
      isOpen: false
    }
  },
  computed: {
    wrapperClasses() {
      return [
        'select-wrapper',
        `select-wrapper--${this.size}`,
        {
          'select-wrapper--floating': this.floating,
          'select-wrapper--disabled': this.disabled,
          'select-wrapper--error': this.error,
          'select-wrapper--open': this.isOpen,
          'select-wrapper--has-value': this.internalValue != null && this.internalValue !== ''
        }
      ]
    },
    displayValue() {
      if (this.internalValue === null || this.internalValue === undefined || this.internalValue === '') return ''
      const item = this.items.find(i => this.getItemValue(i) === this.internalValue)
      return item ? this.getItemTitle(item) : ''
    }
  },
  watch: {
    modelValue(newVal) {
      this.internalValue = newVal
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    getItemTitle(item) {
      return typeof item === 'object' ? item[this.itemTitle] : item
    },
    getItemValue(item) {
      return typeof item === 'object' ? item[this.itemValue] : item
    },
    isSelected(item) {
      return this.getItemValue(item) === this.internalValue
    },
    toggleDropdown() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen
      }
    },
    selectItem(item) {
      const value = this.getItemValue(item)
      this.internalValue = value
      this.$emit('update:modelValue', value)
      this.$emit('change', value)
      this.isOpen = false
    },
    clearSelection(e) {
      e.stopPropagation()
      this.internalValue = null
      this.$emit('update:modelValue', null)
      this.$emit('change', null)
    },
    handleClickOutside(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.select-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;

  &--sm .select__container {
    height: $input-height-sm;
  }
  &--md .select__container {
    height: $input-height-md;
  }
  &--lg .select__container {
    height: $input-height-lg;
  }

  &--disabled {
    opacity: 0.6;
    .select__container {
      background-color: $color-gray-100;
      cursor: not-allowed;
    }
  }

  &--error .select__container {
    border-color: $color-danger;
  }

  &--error.select-wrapper--open .select__container {
    box-shadow: 0 0 0 3px rgba($color-danger, 0.15);
  }

  &--open .select__container {
    border-color: $color-primary;
    box-shadow: 0 0 0 3px $input-focus-ring-color;
  }
}

.select__label {
  display: block;
  margin-bottom: $spacing-2;
  font-size: $font-size-sm;
  font-weight: $font-weight-medium;
  color: $color-text-primary;
}

.select__required {
  color: $color-danger;
  margin-left: 2px;
}

.select__container {
  display: flex;
  align-items: center;
  position: relative;
  background-color: $color-bg-primary;
  border: $input-border-width solid $input-border-color;
  border-radius: $input-border-radius;
  padding: 0 12px;
  cursor: pointer;
  transition: all $transition-normal $transition-timing;
}

.select__prepend {
  display: flex;
  align-items: center;
  margin-right: 8px;
  color: $color-text-secondary;
}

.select__value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: $font-size-sm;
}

.select__selected {
  color: $color-text-primary;
}

.select__placeholder {
  color: $color-text-tertiary;
}

.select__clear {
  display: flex;
  align-items: center;
  color: $color-text-tertiary;
  margin-right: 4px;

  &:hover {
    color: $color-text-secondary;
  }
}

.select__arrow {
  display: flex;
  align-items: center;
  color: $color-text-tertiary;
  transition: transform $transition-normal;

  &--open {
    transform: rotate(180deg);
  }
}

.select__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: $color-bg-primary;
  border: 1px solid $color-border-default;
  border-radius: $input-border-radius;
  box-shadow: $shadow-lg;
  max-height: 240px;
  overflow-y: auto;
  z-index: $z-index-dropdown;
}

.select__option {
  padding: 10px 14px;
  font-size: $font-size-sm;
  cursor: pointer;
  transition: background-color $transition-fast;

  &:hover {
    background-color: $color-gray-100;
  }

  &--selected {
    background-color: $color-primary-50;
    color: $color-primary;
    font-weight: $font-weight-medium;
  }
}

.select__empty {
  padding: 16px;
  text-align: center;
  color: $color-text-tertiary;
  font-size: $font-size-sm;
}

.select__hint {
  margin-top: $spacing-1;
  font-size: $font-size-xs;
  color: $color-text-tertiary;
}

.select__error {
  margin-top: $spacing-1;
  font-size: $font-size-xs;
  color: $color-danger;
}

// ===============================
// Floating Label Styles
// ===============================
.select-wrapper--floating {
  .select__container {
    padding-top: 8px;
    padding-bottom: 0;
  }

  .select__floating-label {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $color-text-tertiary;
    font-size: $font-size-sm;
    font-weight: $font-weight-normal;
    pointer-events: none;
    transition: all $transition-normal $transition-timing;
    background: transparent;
    padding: 0 4px;
    line-height: 1;
    z-index: 1;
  }

  &.select-wrapper--open,
  &.select-wrapper--has-value {
    .select__floating-label {
      top: 0;
      transform: translateY(-50%);
      font-size: 11px;
      font-weight: $font-weight-medium;
      color: $color-primary;
      background: $color-bg-primary;
    }
  }

  &.select-wrapper--has-value:not(.select-wrapper--open) {
    .select__floating-label {
      color: $color-text-secondary;
    }
  }

  &.select-wrapper--error {
    .select__floating-label {
      color: $color-danger;
    }
  }
}

// Dropdown animation
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity $transition-fast, transform $transition-fast;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
